import { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Search } from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { SpecimenCard } from '@/components/gallery/SpecimenCard';
import { PLANTS, TRIBES, TRIBE_COUNTS, cleanTribe } from '@/lib/databank';

export default function Gallery() {
    const reduce = useReducedMotion();
    const [query, setQuery] = useState('');
    const [tribe, setTribe] = useState('All');
    const [sort, setSort] = useState('no');

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = PLANTS.filter((p) => {
            const t = cleanTribe(p.tribe) || 'Unclassified';
            if (tribe !== 'All' && t !== tribe) return false;
            if (!q) return true;
            return [p.botanical_name, p.common_name, p.tribe, p.habit, p.native]
                .some((f) => (f || '').toLowerCase().includes(q));
        });
        list = list.slice();
        if (sort === 'name') list.sort((a, b) => a.botanical_name.localeCompare(b.botanical_name));
        else if (sort === 'tribe') list.sort((a, b) => cleanTribe(a.tribe).localeCompare(cleanTribe(b.tribe)) || a.plant_no - b.plant_no);
        else list.sort((a, b) => a.plant_no - b.plant_no);
        return list;
    }, [query, tribe, sort]);

    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="gallery-page">
            <div className="noise-overlay" aria-hidden="true" />
            <Header />
            <main className="mx-auto max-w-[1500px] px-5 pb-32 md:px-10">
                <motion.div
                    initial={reduce ? false : { opacity: 0, y: 32 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="border-b border-line py-16 md:py-24"
                >
                    <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-terra">Family Asteraceae · Western Vidarbha</p>
                    <h1 className="mt-4 font-serif text-5xl italic leading-none text-ink md:text-8xl">The Index</h1>
                    <p className="mt-6 max-w-xl text-sm leading-relaxed text-ink/70 md:text-base">
                        Every specimen in the databank. Search by botanical name, common name,
                        tribe, habit or native range — or filter the plates by tribe.
                    </p>
                </motion.div>

                {/* search + sort */}
                <div className="flex flex-col gap-4 border-b border-line py-6 md:flex-row md:items-center md:justify-between">
                    <label className="relative flex-1 md:max-w-xl">
                        <Search className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-mute" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            data-testid="gallery-search-input"
                            placeholder="Search the databank…"
                            className="w-full border-b border-line bg-transparent py-3 pl-7 font-mono text-sm tracking-wide text-ink placeholder:text-mute focus:border-terra focus:outline-none"
                        />
                    </label>
                    <div className="flex items-center gap-4">
                        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-mute" data-testid="gallery-result-count">
                            {filtered.length} / {PLANTS.length}
                        </span>
                        <select
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            data-testid="gallery-sort-select"
                            className="cursor-pointer border border-line bg-paper px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink focus:border-terra focus:outline-none"
                        >
                            <option value="no">Sort · Plate no.</option>
                            <option value="name">Sort · Botanical name</option>
                            <option value="tribe">Sort · Tribe</option>
                        </select>
                    </div>
                </div>

                {/* tribe chips */}
                <div className="flex gap-2 overflow-x-auto border-b border-line py-5" data-testid="tribe-filter-chips">
                    {['All'].concat(TRIBES).map((t) => (
                        <button
                            key={t}
                            type="button"
                            onClick={() => setTribe(t)}
                            data-testid={`tribe-chip-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            className={`shrink-0 border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                                tribe === t
                                    ? 'border-forest bg-forest text-paper'
                                    : 'border-line bg-transparent text-ink hover:border-ink'
                            }`}
                        >
                            {t === 'All' ? 'All tribes' : `${t} (${TRIBE_COUNTS[t]})`}
                        </button>
                    ))}
                </div>

                {/* grid */}
                {filtered.length === 0 ? (
                    <div className="py-32 text-center" data-testid="gallery-empty-state">
                        <p className="font-serif text-3xl italic text-mute">No specimens match that search.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4 pt-10 md:grid-cols-3 md:gap-6 lg:grid-cols-4" data-testid="gallery-grid">
                        {filtered.map((p, i) => (
                            <SpecimenCard key={p.plant_no} plant={p} index={i} />
                        ))}
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
}
